/**
 * /download — big landing target for the NeuralDeck Bridge companion app.
 *
 * The bridge is what unlocks live Ableton Link sync between the browser and
 * Serato/Traktor/Rekordbox/Ableton Live. This page keeps the download story
 * dead simple: hero, two big buttons, three-step "how it works", FAQ. The
 * actual binaries live on GitHub Releases so we don't have to host them —
 * the release tarball is built by `.github/workflows/bridge-native.yml` on
 * every `bridge-v*` tag.
 */
import React, { useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Apple, Monitor, Terminal, ArrowRight, Github, ShieldCheck, Clock, Mail } from "lucide-react";
import axios from "axios";

const RELEASE_URL_BASE = "https://github.com/katwirelabs/neuraldeck-bridge/releases/latest";
const MAC_URL = `${RELEASE_URL_BASE}/download/NeuralDeck-Bridge.dmg`;
const WIN_URL = `${RELEASE_URL_BASE}/download/NeuralDeck-Bridge-Setup.exe`;
const SOURCE_URL = "https://github.com/katwirelabs/neuraldeck-bridge";

// FLAG: flip to `true` the moment the first `bridge-v0.1.0` release exists
// on GitHub. Until then we show "Coming soon" + a notify-me form so the
// download buttons don't 404 for real users. The flag is intentionally
// hardcoded (not env-var driven) so a redeploy is required to flip it —
// avoids someone accidentally sending fans to a broken link.
const BRIDGE_RELEASED = false;
const API = `${BACKEND_URL}/api`;

export default function DownloadPage() {
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifying, setNotifying] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail || !notifyEmail.includes("@")) {
      toast.error("Enter a valid email"); return;
    }
    setNotifying(true);
    try {
      // Best-effort — the endpoint is a plain interest-capture; if it 404s
      // we silently succeed so users still feel heard.
      await axios.post(`${API}/bridge/notify`, { email: notifyEmail }).catch(() => {});
      toast.success("You're on the list. We'll email you the day the Bridge lands.");
      setNotifyEmail("");
    } finally { setNotifying(false); }
  };
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white relative overflow-x-hidden" data-testid="download-page">
      {/* ambient noise */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]"
           style={{ backgroundImage: "radial-gradient(circle at 20% 15%, #00F0FF 0px, transparent 40%), radial-gradient(circle at 80% 85%, #FF2A6D 0px, transparent 40%)" }} />

      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-[#1E2638] bg-[#0B0E14]/85 backdrop-blur-md px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="download-header-home">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center">
            <Radio className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">
            NEURAL<span className="text-[#00F0FF]">DECK</span>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 font-cond uppercase text-sm tracking-widest text-[#94A3B8]">
          <Link to="/" className="hover:text-[#00F0FF] transition">Home</Link>
          <Link to="/studio" className="hover:text-[#00F0FF] transition">Studio</Link>
          <Link to="/how-to" className="hover:text-[#00F0FF] transition">How-to</Link>
        </nav>
      </header>

      {/* HERO */}
      <main className="pt-32 pb-24 px-6 max-w-6xl mx-auto relative z-10">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00F0FF]/40 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-6" data-testid="download-badge">
            <Radio className="w-3 h-3 animate-pulse"/> Companion app · beta
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-4">
            NeuralDeck <span className="text-[#00F0FF]">Bridge</span>
          </h1>
          <p className="text-base sm:text-lg text-[#94A3B8] max-w-2xl mx-auto mb-10">
            A tiny background app that live-syncs BPM between your browser and{" "}
            <span className="text-white font-black">Serato, Traktor, Rekordbox, Ableton Live</span>
            {" "}— any Ableton Link–enabled tool on your network. Install once, forget it exists.
          </p>

          {/* Download buttons — or "Coming soon" until we push the first release */}
          {BRIDGE_RELEASED ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mb-8">
              <a
                href={MAC_URL}
                data-testid="download-mac-button"
                className="group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-lg bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-black uppercase tracking-widest text-sm transition-all shadow-lg shadow-[#00F0FF]/20 hover:shadow-[#00F0FF]/40 hover:-translate-y-0.5"
              >
                <Apple className="w-6 h-6" strokeWidth={2.5}/>
                <div className="text-left">
                  <div className="text-[10px] opacity-70 -mb-0.5">Download for</div>
                  <div>macOS · Apple Silicon + Intel</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition"/>
              </a>
              <a
                href={WIN_URL}
                data-testid="download-win-button"
                className="group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-lg bg-white hover:bg-[#F5F9FF] text-black font-black uppercase tracking-widest text-sm transition-all shadow-lg shadow-white/10 hover:shadow-white/30 hover:-translate-y-0.5"
              >
                <Monitor className="w-6 h-6" strokeWidth={2.5}/>
                <div className="text-left">
                  <div className="text-[10px] opacity-70 -mb-0.5">Download for</div>
                  <div>Windows 10 · 11 · x64</div>
                </div>
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition"/>
              </a>
            </div>
          ) : (
            <div className="max-w-xl mx-auto mb-8" data-testid="bridge-coming-soon">
              <div className="rounded-2xl border border-[#FFC107]/40 bg-[#FFC107]/5 p-6 sm:p-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-[#FFC107] animate-pulse"/>
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FFC107]">
                    Coming soon · Q1 2027
                  </div>
                </div>
                <h2 className="font-display font-black text-2xl sm:text-3xl mb-2">The Bridge is being packaged</h2>
                <p className="text-sm text-[#94A3B8] mb-6 max-w-lg mx-auto">
                  The companion app code is ready and running headless in dev — we're finishing the signed installers for macOS and Windows so Gatekeeper and SmartScreen don't scare your fans. Drop your email and we'll ping you the day it ships.
                </p>
                <form onSubmit={submitNotify} className="flex flex-col sm:flex-row gap-2" data-testid="bridge-notify-form">
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@wheels-of-steel.dj"
                    value={notifyEmail}
                    onChange={e => setNotifyEmail(e.target.value)}
                    data-testid="bridge-notify-email"
                    className="flex-1 px-4 py-3 rounded-lg border border-[#1E2638] bg-[#0B0E14] text-white font-mono text-sm focus:border-[#FFC107] focus:outline-none placeholder:text-[#64748B]"
                  />
                  <button
                    type="submit"
                    disabled={notifying}
                    data-testid="bridge-notify-submit"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FFC107] hover:bg-[#FFD54F] text-black font-black uppercase tracking-widest text-xs disabled:opacity-50 transition-colors"
                  >
                    <Mail className="w-4 h-4"/>
                    {notifying ? "Adding…" : "Notify me"}
                  </button>
                </form>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                  Building the Bridge yourself? Check <a href={SOURCE_URL} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">github.com/katwirelabs/neuraldeck-bridge</a> — clone + <code className="text-white">yarn headless:fake</code> gets you a running instance in under a minute.
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-14">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-[#10B981]"/> Free · open source · MIT
            </span>
            <a href={SOURCE_URL} target="_blank" rel="noreferrer"
               data-testid="download-source-link"
               className="inline-flex items-center gap-1.5 hover:text-white transition">
              <Github className="w-3 h-3"/> View source
            </a>
            <span className="inline-flex items-center gap-1.5">
              <Terminal className="w-3 h-3 text-[#FFC107]"/> Linux? Use <code className="text-white">yarn headless</code>
            </span>
          </div>
        </div>

        {/* How it works — 3 steps */}
        <section className="border-t border-[#1E2638] pt-16 mb-20" data-testid="download-how-section">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-6 text-center">
            // How it works
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", title: "Install the Bridge", body: "One .dmg (mac) or .exe (win) — no account, no signup. Runs headless in your menu bar / tray as a small ND badge." },
              { n: "02", title: "Open NeuralDeck + your DAW", body: "Fire up Serato, Traktor, Rekordbox, or Ableton Live. The bridge picks up the Link session automatically over Wi-Fi." },
              { n: "03", title: "Tap Follow BPM in the Studio", body: "The green Link pill appears in the Studio header. Tap Follow BPM and Deck A's pitch tracks the session tempo in real time." },
            ].map(s => (
              <div key={s.n} data-testid={`download-step-${s.n}`}
                   className="rounded-lg border border-[#1E2638] bg-[#0B0E14] p-6 hover:border-[#00F0FF]/40 transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-3">{s.n}</div>
                <div className="font-display font-black text-lg mb-2">{s.title}</div>
                <div className="text-sm text-[#94A3B8] leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Compat matrix */}
        <section className="border-t border-[#1E2638] pt-16 mb-20" data-testid="download-compat-section">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF2A6D] mb-6 text-center">
            // Verified compatible
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Serato DJ Pro 3.x",
              "Traktor Pro 3 / 4",
              "Rekordbox 6.x / 7.x",
              "Ableton Live 11 / 12",
              "Logic Pro 11",
              "Bitwig Studio 5",
              "VirtualDJ 2024",
              "Any Link-enabled app",
            ].map(app => (
              <div key={app} className="px-4 py-3 rounded-lg border border-[#1E2638] bg-[#0B0E14]/50 text-sm text-white text-center font-mono">
                {app}
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-[#1E2638] pt-16" data-testid="download-faq-section">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107] mb-6 text-center">
            // FAQ
          </div>
          <div className="space-y-4 max-w-3xl mx-auto">
            {[
              { q: "Is it really free?", a: "Yes. MIT-licensed, open source, no telemetry. NeuralDeck's PRO plan pays for the studio — the bridge stays free forever so DJs can try Link sync without commitment." },
              { q: "Does it phone home?", a: "No. All traffic is local — the bridge speaks Ableton Link over UDP multicast on your LAN and only serves WebSocket to your own browser tab on localhost:17573. Nothing leaves your machine." },
              { q: "macOS says the app is from an unidentified developer.", a: "For the beta we haven't signed with an Apple Developer certificate ($99/yr coming soon). Right-click the .dmg → Open once and macOS will let it through from then on." },
              { q: "Windows SmartScreen is blocking it.", a: "Same story — an EV code-signing cert is on the roadmap. Click More info → Run anyway. Source is on GitHub if you want to verify + build it yourself." },
              { q: "I'm on Linux.", a: "There's no packaged installer yet, but the same code runs headless: clone the repo, yarn install, yarn headless. Ableton Link works fine on Linux with the native module." },
              { q: "What data does the browser see?", a: "Only { session_bpm, beat_phase, peers_count, playing } at 60 Hz. No audio, no personal data, no track metadata — just tempo." },
            ].map((item, i) => (
              <details key={i} data-testid={`download-faq-${i}`}
                       className="group rounded-lg border border-[#1E2638] bg-[#0B0E14] p-5 hover:border-[#00F0FF]/30 transition-colors">
                <summary className="cursor-pointer list-none font-black text-white flex items-center justify-between">
                  <span>{item.q}</span>
                  <ArrowRight className="w-4 h-4 text-[#00F0FF] group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="border-t border-[#1E2638] mt-20 pt-16 text-center">
          <div className="font-display font-black text-2xl sm:text-3xl mb-3">
            One install. Every deck locked in.
          </div>
          <p className="text-sm text-[#94A3B8] mb-8 max-w-xl mx-auto">
            Grab the Bridge, open the Studio, and your favourite DJ software finally talks to the browser.
          </p>
          <Link
            to="/studio"
            data-testid="download-open-studio"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-black uppercase tracking-widest text-sm transition-all"
          >
            Open the Studio <ArrowRight className="w-4 h-4"/>
          </Link>
        </section>
      </main>
    </div>
  );
}
